import React, { useState, useEffect, useMemo } from 'react';
import { useErrand } from '../contexts/ErrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import StashitLogo from '../components/Logo/StashitLogo';
import ChatWidget from '../components/Chat/ChatWidget';
import EmptyState from '../components/UI/EmptyState';
import { ErrandCardSkeleton } from '../components/UI/LoadingSkeleton';
import JobFilters from '../components/JobFilters';
import JobCard from '../components/JobCard';
import { getErrorMessage } from '../utils/errorMessages';
import { showSuccess, showError } from '../utils/toast';

const ErrandManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { notifications } = useNotifications();

  const {
    availableErrands,
    driverErrands,
    updateErrandStatus,
    fetchAvailableErrands,
    fetchDriverErrands,
    loading,
    error,
    clearError
  } = useErrand();

  const [activeTab, setActiveTab] = useState<'available' | 'accepted'>('available');
  const [activeChat, setActiveChat] = useState<{ errandId: string; title: string } | null>(null);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(10);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationPermissionGranted(true);
        },
        (error) => {
          // Handle geolocation errors gracefully
          if (error.code === error.PERMISSION_DENIED) {
            console.log('Geolocation permission denied by user');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            console.log('Geolocation position unavailable');
          } else if (error.code === error.TIMEOUT) {
            console.log('Geolocation request timed out');
          } else {
            console.log('Geolocation error:', error.message || error);
          }
          setLocationPermissionGranted(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    }
  }, []);

  useEffect(() => {
    if (userLocation) {
      fetchAvailableErrands(userLocation.latitude, userLocation.longitude, radiusKm);
    } else {
      fetchAvailableErrands(); // Fetch all errands if no location
    }
    fetchDriverErrands();
  }, [fetchAvailableErrands, fetchDriverErrands, userLocation, radiusKm]);

  // Refresh available errands when a new errand notification is received
  useEffect(() => {
    const newErrandNotification = notifications.find(
      n => !n.is_read && n.data?.action === 'new_errand'
    );

    if (newErrandNotification) {
      console.log('New errand notification received, refreshing available errands...');
      // Refresh available errands
      if (userLocation) {
        fetchAvailableErrands(userLocation.latitude, userLocation.longitude, radiusKm);
      } else {
        fetchAvailableErrands();
      }
    }
  }, [notifications, fetchAvailableErrands, userLocation, radiusKm]);

  // Refresh when page becomes visible (user navigates back to this page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, refreshing available errands...');
        if (userLocation) {
          fetchAvailableErrands(userLocation.latitude, userLocation.longitude, radiusKm);
        } else {
          fetchAvailableErrands();
        }
        fetchDriverErrands();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchAvailableErrands, fetchDriverErrands, userLocation, radiusKm]);

  const handleAcceptErrand = async (errand: any) => {
    try {
      const success = await updateErrandStatus({
        errandId: errand.id,
        status: 'accepted',
        notes: 'Errand accepted by driver'
      });

      if (success) {
        showSuccess('Errand accepted successfully!');
        // Switch to "My Errands" tab to show the accepted errand
        setActiveTab('accepted');
        // Refresh available errands to remove the accepted one
        if (userLocation) {
          fetchAvailableErrands(userLocation.latitude, userLocation.longitude, radiusKm);
        } else {
          fetchAvailableErrands();
        }
      } else {
        showError('Failed to accept errand. Please try again.');
      }
    } catch (error) {
      console.error('Error accepting errand:', error);
      showError(getErrorMessage(error, 'Failed to accept errand. Please try again.'));
    }
  };

  const handleMarkAsPaid = async (errand: any) => {
    try {
      const success = await updateErrandStatus({
        errandId: errand.id,
        status: 'paid',
        notes: 'Cash payment received from customer'
      });

      if (success) {
        showSuccess('Payment marked as received! Customer will be notified.');
        // Refresh errands list
        fetchDriverErrands();
        // Trigger a custom event to refresh earnings in other components
        window.dispatchEvent(new CustomEvent('errandPaymentMarked', { detail: { errandId: errand.id } }));
      } else {
        showError('Failed to mark payment as received. Please try again.');
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
      showError(getErrorMessage(error, 'Failed to mark payment as received. Please try again.'));
    }
  };

  const handleCompleteErrand = async (errand: any) => {
    console.log('handleCompleteErrand called with errand:', errand);

    try {
      const success = await updateErrandStatus({
        errandId: errand.id,
        status: 'completed',
        notes: 'Errand completed successfully'
      });

      console.log('updateErrandStatus result:', success);

      if (success) {
        showSuccess('Errand completed! Customer will be notified.');
        // Refresh errands list
        fetchDriverErrands();
        // Trigger event to refresh earnings
        window.dispatchEvent(new CustomEvent('errandCompleted', { detail: { errandId: errand.id } }));
      } else {
        console.error('Failed to complete errand');
        showError('Failed to complete errand. Please try again.');
      }
    } catch (error) {
      console.error('Error completing errand:', error);
      showError(getErrorMessage(error, 'Failed to complete errand. Please try again.'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Please log in to access errand management</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <StashitLogo width={200} height={60} showTagline={false} />
        </div>
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          Errand Management
        </h1>
        <p className="text-neutral-600">
          Manage available errands and track your accepted tasks
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={clearError} className="text-red-600 hover:text-red-800 text-sm mt-2">
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-neutral-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('available')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'available'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          Available Errands ({availableErrands.length})
        </button>
        <button
          onClick={() => setActiveTab('accepted')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'accepted'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          My Errands ({driverErrands.length})
        </button>
      </div>

      {/* Enhanced Job Filters */}
      {activeTab === 'available' && (
        <JobFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          radiusKm={radiusKm}
          setRadiusKm={setRadiusKm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          totalJobs={availableErrands.length}
          filteredJobs={availableErrands.length} // TODO: Calculate filtered count
        />
      )}

      {/* Available Jobs Tab with Enhanced Cards */}
      {activeTab === 'available' && (
        <div className="space-y-6">
          {loading && availableErrands.length === 0 ? (
            <div className="space-y-4">
              <ErrandCardSkeleton />
              <ErrandCardSkeleton />
              <ErrandCardSkeleton />
            </div>
          ) : (() => {
            // Apply filters and sorting
            let filteredAndSorted = availableErrands.filter(errand => {
              // Text search
              const searchMatch = !searchQuery ||
                [errand.title, errand.description, errand.pickup_location, errand.dropoff_location, errand.errand_types?.name]
                .some(field => field?.toLowerCase().includes(searchQuery.toLowerCase()));

              // Status filter (for available errands, status is always 'pending')
              const statusMatch = statusFilter === 'all' || statusFilter === 'pending';

              return searchMatch && statusMatch;
            });

            // Apply sorting
            filteredAndSorted.sort((a, b) => {
              switch (sortBy) {
                case 'newest':
                  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case 'oldest':
                  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                case 'highest_pay':
                  return (b.driver_earnings || 0) - (a.driver_earnings || 0);
                case 'lowest_pay':
                  return (a.driver_earnings || 0) - (b.driver_earnings || 0);
                case 'closest':
                  // TODO: Implement distance sorting when location data is available
                  return 0;
                default:
                  return 0;
              }
            });

            if (availableErrands.length === 0) {
              return (
                <EmptyState
                  icon="📋"
                  title="No Available Jobs"
                  message="There are no jobs available at the moment. Check back later for new requests."
                />
              );
            }

            if (filteredAndSorted.length === 0) {
              return (
                <EmptyState
                  icon="🔍"
                  title="No Results Found"
                  message={`No jobs match your current filters. Try adjusting your search or filters.`}
                  onAction={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setRadiusKm(10);
                  }}
                  actionLabel="Clear Filters"
                />
              );
            }

            return (
              <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                {filteredAndSorted.map((errand) => (
                  <JobCard
                    key={errand.id}
                    job={errand}
                    onAccept={() => handleAcceptErrand(errand)}
                    onChat={() => setActiveChat({ errandId: errand.id, title: errand.title })}
                    loading={loading}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Accepted Errands Tab */}
      {activeTab === 'accepted' && (
        <div className="space-y-4">
          {loading && driverErrands.length === 0 ? (
            <div className="space-y-4">
              <ErrandCardSkeleton />
              <ErrandCardSkeleton />
            </div>
          ) : driverErrands.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No Accepted Errands"
              message="Accept errands from the Available tab to get started. Your accepted errands will appear here."
              onAction={() => setActiveTab('available')}
              actionLabel="View Available Errands"
            />
          ) : (
            driverErrands.map((errand) => (
              <div key={errand.id} className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-800">{errand.title}</h3>
                    <p className="text-sm text-neutral-600">
                      {errand.errand_types?.name} • {errand.errand_types?.description}
                    </p>
                    {errand.courier_company && (
                      <p className="text-xs text-blue-600 mt-1">
                        🏢 Company: {errand.courier_company.name}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(errand.status)}`}>
                      {errand.status.replace('_', ' ').toUpperCase()}
                    </div>
                    {errand.payment_status && (
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getPaymentStatusColor(errand.payment_status)}`}>
                        {errand.payment_status === 'completed' ? '💰 Paid' : '⏳ Pending'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-medium text-neutral-700 mb-2">Pickup Location</h4>
                    <p className="text-neutral-800">{errand.pickup_location}</p>
                  </div>
                  {errand.dropoff_location && (
                    <div>
                      <h4 className="font-medium text-neutral-700 mb-2">Drop-off Location</h4>
                      <p className="text-neutral-800">{errand.dropoff_location}</p>
                    </div>
                  )}
                </div>

                {/* Payment Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-blue-800">Payment Method:</span>
                      <span className="ml-2 text-sm text-blue-700">
                        {errand.payment_method === 'cash_on_delivery' ? '💰 Cash on Delivery' : '💳 Wallet'}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-blue-800">
                        <span className="font-medium">Total:</span> P{errand.total_cost?.toFixed(2) || 'TBD'}
                      </div>
                      <div className="text-xs text-blue-600">
                        Your Earnings: P{errand.driver_earnings?.toFixed(2) || 'TBD'}
                      </div>
                    </div>
                  </div>
                  {errand.payment_method === 'cash_on_delivery' && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-xs text-yellow-800">
                        💰 <strong>Cash Collection:</strong> Collect P{errand.total_cost?.toFixed(2) || 'TBD'} from the customer when errand is completed.
                      </p>
                    </div>
                  )}
                </div>

                <div className="text-sm text-neutral-600 mb-4">
                  <p>Customer: {errand.profiles?.full_name}</p>
                  <p>Contact: {errand.profiles?.phone}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setActiveChat({ errandId: errand.id, title: errand.title })}
                    className="btn-primary"
                  >
                    💬 Chat
                  </button>

                  <div className="flex space-x-2">
                    {errand.payment_method === 'cash_on_delivery' && errand.payment_status === 'pending' && (
                      <button
                        onClick={() => handleMarkAsPaid(errand)}
                        disabled={loading}
                        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        💰 Mark as Paid
                      </button>
                    )}

                    {errand.status !== 'completed' && (
                      <button
                        onClick={() => {
                          console.log('Complete Errand button clicked for errand:', errand.id);
                          handleCompleteErrand(errand);
                        }}
                        disabled={loading}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ✅ Complete Errand
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Chat Widget */}
      {activeChat && (
        <ChatWidget
          errandId={activeChat!.errandId}
          errandTitle={activeChat!.title}
          isOpen={true}
          onToggle={(isOpen) => {
            // Only remove the chat if it's completely closed, not just minimized
            if (!isOpen) {
              setActiveChat(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default ErrandManagementPage;
